import type { ApiResponse, Pagination } from "@/types";

import { BaseApi } from "../base.api";
import type { Course } from "../courses/types";

/**
 * `MOCK` — demo rels (`PAYMENTS_MOCK_ENABLED`). Prod'da hech qachon
 * yozilmaydi, lekin stend bazasidagi qatorlarni reyestrda ko'rsatish va
 * filtrlash uchun tip bilishi kerak.
 */
export type PaymentProvider = "PAYME" | "CLICK" | "MANUAL" | "MOCK" | "CARD";

/** O'quvchi to'lovni boshlay oladigan yo'llar — MANUAL faqat admin uchun. */
export const CHECKOUT_PROVIDERS = ["PAYME", "CLICK"] as const;
export type CheckoutProvider = (typeof CHECKOUT_PROVIDERS)[number];

/**
 * Tranzaksiyaning hayot sikli. Raqamlar — Payme'ning o'z holat qiymatlari;
 * Click va qo'lda kiritilgan to'lovlar ham shu shkalaga solingan, shuning
 * uchun uchala yo'lga bitta `switch` yetadi.
 */
export const PaymentState = {
  CREATED: 1,
  PAID: 2,
  CANCELLED: -1,
  CANCELLED_AFTER_PAID: -2,
} as const;

export type PaymentStateValue = (typeof PaymentState)[keyof typeof PaymentState];

export interface Payment {
  id: number;
  provider: PaymentProvider;
  providerTransactionId: string | null;
  providerPaydocId: string | null;
  state: PaymentStateValue;
  reason: number | null;
  /** To'lov summasi — BUTUN so'm. */
  amountUzs: number;
  createTimeMs: number;
  performTimeMs: number;
  cancelTimeMs: number;
  enrollmentId: number;
  courseId: number;
  studentId: number;
  note: string | null;
  confirmedById: number | null;
  /** CARD: chek rasmi (media proksisi havolasi), to'lovchi kartasi va yuklangan payt. */
  receiptUrl?: string | null;
  payerCardLast4?: string | null;
  receiptSubmittedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  course?: Pick<Course, "id" | "title" | "thumbnail"> | null;
  student?: { id: number; firstName: string; lastName: string } | null;
}

export interface CheckoutData {
  courseId: number;
  provider: CheckoutProvider;
  /** Provayder brauzerni qaytaradigan manzil — frontend yo'li ko'rinishida. */
  returnPath?: string;
}

export interface CheckoutResult {
  enrollmentId: number;
  courseId: number;
  provider: PaymentProvider;
  /** To'lanadigan summa — BUTUN so'm. */
  amountUzs: number;
  /** Brauzer shu manzilga yuboriladi. */
  paymentUrl: string;
}

export interface ConfirmManualPaymentData {
  enrollmentId: number;
  /** Qabul qilingan summa — BUTUN so'm. Berilmasa backend kurs narxini oladi. */
  amountUzs?: number;
  note?: string;
  reference?: string;
}

export interface PaymentFilter {
  page?: number;
  limit?: number;
  provider?: PaymentProvider;
  state?: PaymentStateValue;
  courseId?: number;
}

// ─── Kartaga to'lov ──────────────────────────────────────────────────────────

export type CardPaymentStatus =
  "AWAITING_RECEIPT" | "UNDER_REVIEW" | "APPROVED" | "REJECTED" | "CANCELLED" | "REFUNDED";

/** Chek navbatidagi qator — backend'dagi `CardReviewRow`. */
export interface CardReviewRow {
  id: number;
  orderCode: string;
  status: CardPaymentStatus;
  amountUzs: number;
  courseId: number;
  enrollmentId: number;
  course: { id: number; title: string; slug: string } | null;
  payTo: { cardNumber: string; cardNumberFormatted: string; cardHolder: string } | null;
  receiptUrl: string | null;
  payerCardLast4: string | null;
  receiptSubmittedAt: string | null;
  rejectReason: string | null;
  createdAt: string;
  reviewedAt: string | null;
  student: { id: number; firstName: string; lastName: string; email: string } | null;
}

// ─── Pulni qaytarish ─────────────────────────────────────────────────────────

export type RefundStatus = "REQUESTED" | "REFUNDED" | "REJECTED" | "CANCELLED";

export interface RefundRequest {
  id: number;
  paymentId: number;
  studentId: number;
  courseId: number;
  amountUzs: number;
  consumedPercent: number;
  reason: string;
  cardNumber: string;
  cardHolder: string;
  status: RefundStatus;
  /** Shu paytgacha qaytarilishi kerak (so'rov + 3 ish kuni). */
  dueAt: string;
  processedAt: string | null;
  adminNote: string | null;
  createdAt: string;
  course?: { id: number; title: string; slug: string } | null;
  student?: { id: number; firstName: string; lastName: string; email: string } | null;
  processedBy?: { id: number; firstName: string; lastName: string } | null;
}

/** To'lovlar API — backend'dagi `PaymentsController` ning ko'zgusi. */
export class PaymentsApi extends BaseApi {
  private readonly baseUrl: string = "/payments";

  /** Kurs uchun to'lovni boshlaydi; javobda yo'naltiriladigan manzil keladi. */
  checkout(data: CheckoutData) {
    return this.request.post<CheckoutResult, ApiResponse<CheckoutResult>>(
      `${this.baseUrl}/checkout`,
      data,
    );
  }

  myPayments(filter: PaymentFilter) {
    return this.request.get<Payment[], Pagination<Payment>>(`${this.baseUrl}/my`, {
      params: filter,
    });
  }

  /** Bitta kursning to'lovlari — kurs egasi yoki xodim uchun. */
  coursePayments(courseId: number, filter: PaymentFilter) {
    return this.request.get<Payment[], Pagination<Payment>>(`${this.baseUrl}/course/${courseId}`, {
      params: filter,
    });
  }

  // ─── Xodimlar ──────────────────────────────────────────────────────────────

  /** To'liq reyestr (moderator / admin). */
  list(filter: PaymentFilter) {
    return this.request.get<Payment[], Pagination<Payment>>(this.baseUrl, { params: filter });
  }

  /** Naqd yoki bank orqali kelgan to'lovni qayd etadi va kursni ochadi (admin). */
  confirmManual(data: ConfirmManualPaymentData) {
    return this.request.post<Payment, ApiResponse<Payment>>(`${this.baseUrl}/manual`, data);
  }

  // ─── Kartaga to'lov (admin) ────────────────────────────────────────────────

  /** Tasdiq kutayotgan cheklar — eng eskisi birinchi. */
  cardReviewQueue(filter: { page?: number; limit?: number }) {
    return this.request.get<CardReviewRow[], Pagination<CardReviewRow>>(
      `${this.baseUrl}/card/review`,
      { params: filter },
    );
  }

  cardHistory(filter: { page?: number; limit?: number; status?: "PAID" | "REJECTED" }) {
    return this.request.get<CardReviewRow[], Pagination<CardReviewRow>>(
      `${this.baseUrl}/card/history`,
      { params: filter },
    );
  }

  /** Chekni tasdiqlaydi va kursni ochadi — avval bank ilovasida pulni tekshiring. */
  approveCard(id: number) {
    return this.request.post<CardReviewRow, ApiResponse<CardReviewRow>>(
      `${this.baseUrl}/card/${id}/approve`,
    );
  }

  rejectCard(id: number, reason: string) {
    return this.request.post<CardReviewRow, ApiResponse<CardReviewRow>>(
      `${this.baseUrl}/card/${id}/reject`,
      { reason },
    );
  }

  // ─── Pulni qaytarish so'rovlari (admin) ────────────────────────────────────

  refundRequests(filter: { page?: number; limit?: number; status?: RefundStatus }) {
    return this.request.get<RefundRequest[], Pagination<RefundRequest>>(`${this.baseUrl}/refunds`, {
      params: filter,
    });
  }

  /** Pul o'tkazildi: to'lov qaytariladi va kurs yopiladi. */
  completeRefund(id: number, note?: string) {
    return this.request.post<RefundRequest, ApiResponse<RefundRequest>>(
      `${this.baseUrl}/refunds/${id}/complete`,
      { note },
    );
  }

  rejectRefund(id: number, reason: string) {
    return this.request.post<RefundRequest, ApiResponse<RefundRequest>>(
      `${this.baseUrl}/refunds/${id}/reject`,
      { reason },
    );
  }

  /** To'langan tranzaksiyani qaytaradi va kirish huquqini bekor qiladi (admin). */
  refund(id: number, note?: string) {
    return this.request.post<Payment, ApiResponse<Payment>>(`${this.baseUrl}/${id}/refund`, {
      note,
    });
  }
}

export const paymentsApi = new PaymentsApi();
